// FÁZE 3 — Play: scénáře A/B/C + realizační nástroje.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Target, SoccerBall, PlayCircle, GridFour, Star, Plus, Trash, GitBranch, Tag, Crown } from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";
import { listScenarios, createScenario, deleteScenario, listFocuses, searchPlayers } from "../../lib/lab-api";

const TOOLS = [
    { to: "/planner", label: "Transfer Planner", icon: Target, text: "Načti FPL tým, what-if výměny, plán přestupů po GW" },
    { to: "/captain", label: "Kapitánský plánovač", icon: Crown, text: "Matice kapitánů GW po GW dle Prediction Modelu v2" },
    { to: "/lineup-sim", label: "Lineup Sim", icon: SoccerBall, text: "Sestav 15 hráčů a simuluj body" },
    { to: "/lineup-sim/top-picks", label: "Top Picks", icon: Star, text: "Žebříček tutovek dle modelu" },
    { to: "/tools/season-simulator", label: "Season Simulator", icon: PlayCircle, text: "Simulace celé sezóny vč. přestupů" },
    { to: "/tools/fixture-matrix", label: "Fixture Matrix", icon: GridFour, text: "Projekce bodů hráč × GW" },
];
const inputCls = "w-full bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[11px] focus:outline-none focus:border-fa-mint placeholder:text-white/25";

function ScenarioForm({ focuses, onDone, onCancel }) {
    const [f, setF] = useState({ name: "", focus_id: "", gw_start: 1, gw_end: 6, chip: "", note: "" });
    const [transfers, setTransfers] = useState([]);
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (q.trim().length < 2) { setResults([]); return; }
        const h = setTimeout(() => searchPlayers(q.trim(), 5).then((d) => setResults(d.results || [])).catch(() => {}), 250);
        return () => clearTimeout(h);
    }, [q]);

    const save = async () => {
        if (!f.name.trim()) { toast.error("Zadej název scénáře"); return; }
        setBusy(true);
        try {
            await createScenario({
                name: f.name, focus_id: f.focus_id || null,
                planned_transfers: transfers,
                gw_start: Number(f.gw_start), gw_end: Number(f.gw_end),
                chip: f.chip || null, note: f.note || null,
            });
            toast.success("Scénář uložen");
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
        finally { setBusy(false); }
    };

    return (
        <div className="border border-fa-sky/40 bg-black/50 p-4 space-y-3" data-testid="scenario-form">
            <div className="font-mono text-[10px] uppercase tracking-widest text-fa-sky">Nový scénář</div>
            <div className="grid md:grid-cols-4 gap-2">
                <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Scénář A — koupit Salaha" className={inputCls} data-testid="scenario-name" />
                <select value={f.focus_id} onChange={(e) => setF({ ...f, focus_id: e.target.value })} className={inputCls} data-testid="scenario-focus">
                    <option value="">Bez focusu</option>
                    {focuses.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                </select>
                <div className="flex gap-1">
                    <input type="number" min={1} max={38} value={f.gw_start} onChange={(e) => setF({ ...f, gw_start: e.target.value })} className={inputCls} data-testid="scenario-gw-start" />
                    <input type="number" min={1} max={38} value={f.gw_end} onChange={(e) => setF({ ...f, gw_end: e.target.value })} className={inputCls} data-testid="scenario-gw-end" />
                </div>
                <select value={f.chip} onChange={(e) => setF({ ...f, chip: e.target.value })} className={inputCls} data-testid="scenario-chip">
                    <option value="">Bez chipu</option>
                    <option value="wildcard">Wildcard</option><option value="freehit">Free Hit</option>
                    <option value="benchboost">Bench Boost</option><option value="triplecaptain">Triple Captain</option>
                </select>
            </div>
            <div className="relative">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Přidat plánovaný nákup (hledej hráče)…" className={inputCls} data-testid="scenario-add-transfer" />
                {results.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-[#0b0b0b] border border-fa-violet/40 z-30">
                        {results.map((p) => (
                            <button key={p.fpl_id} type="button"
                                    onClick={() => { setTransfers([...transfers, { in_fpl_id: p.fpl_id, in_name: p.name, price: p.price }]); setQ(""); setResults([]); }}
                                    data-testid={`scenario-pick-${p.fpl_id}`}
                                    className="w-full text-left px-2.5 py-1.5 flex justify-between border-b border-white/5 hover:bg-fa-violet/20 font-mono text-[10px]">
                                <span className="font-display font-bold text-xs">{p.name}</span>
                                <span className="text-white/40">{p.team} · £{p.price}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {transfers.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {transfers.map((t, i) => (
                        <span key={i} className="font-mono text-[9px] px-2 py-1 border border-fa-sky/40 text-fa-sky flex items-center gap-1.5">
                            IN: {t.in_name} (£{t.price})
                            <button type="button" onClick={() => setTransfers(transfers.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-400">×</button>
                        </span>
                    ))}
                </div>
            )}
            <div className="flex gap-2">
                <button type="button" onClick={save} disabled={busy} data-testid="scenario-save-btn"
                        className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 bg-fa-sky text-black font-bold disabled:opacity-40">
                    {busy ? "Ukládám…" : "Uložit scénář"}
                </button>
                <button type="button" onClick={onCancel} className="font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-white/15 text-white/60">Zrušit</button>
            </div>
        </div>
    );
}

export default function LabPlay() {
    const { user, login } = useAuth();
    const [scenarios, setScenarios] = useState([]);
    const [focuses, setFocuses] = useState([]);
    const [creating, setCreating] = useState(false);

    const load = useCallback(() => {
        if (!user) return;
        listScenarios().then((d) => setScenarios(d.items || [])).catch(() => {});
        listFocuses().then((d) => setFocuses(d.items || [])).catch(() => {});
    }, [user]);
    useEffect(() => { load(); }, [load]);

    const remove = async (s) => {
        try { await deleteScenario(s.id); toast.success("Scénář smazán"); load(); }
        catch { toast.error("Smazání selhalo"); }
    };

    return (
        <div className="space-y-8" data-testid="lab-play">
            <section className="space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="font-display font-black uppercase text-base flex items-center gap-2">
                        <GitBranch size={16} className="text-fa-sky" /> Scénáře A / B / C
                    </h2>
                    {user && !creating && (
                        <button type="button" onClick={() => setCreating(true)} data-testid="scenario-create-btn"
                                className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 bg-fa-sky text-black font-bold flex items-center gap-1.5">
                            <Plus size={12} weight="bold" /> Create Scenario
                        </button>
                    )}
                </div>
                <p className="font-mono text-[10px] text-white/50 max-w-2xl">
                    Porovnej varianty rozhodnutí: koupit hráče A, koupit hráče B, nebo přestup odložit.
                </p>
                {!user && (
                    <div className="border border-white/10 bg-black/40 p-5 font-mono text-[11px] text-white/60" data-testid="play-login-cta">
                        Pro scénáře se <button type="button" onClick={login} className="text-fa-mint underline">přihlas</button>.
                    </div>
                )}
                {creating && <ScenarioForm focuses={focuses} onDone={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} />}
                {scenarios.length > 0 && (
                    <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto" data-testid="scenario-table">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-widest text-white/40">
                                    <th className="px-3 py-2">Scénář</th><th className="px-3 py-2">Okno</th>
                                    <th className="px-3 py-2">Nákupy</th><th className="px-3 py-2 text-right">Cena IN</th>
                                    <th className="px-3 py-2 text-right">Body 25/26 IN</th><th className="px-3 py-2 text-right">Ø FDR IN</th>
                                    <th className="px-3 py-2">Chip</th><th className="px-3 py-2">Riziko</th><th className="px-3 py-2" />
                                </tr>
                            </thead>
                            <tbody>
                                {scenarios.map((s) => (
                                    <tr key={s.id} className="border-b border-white/5 font-mono text-[11px]" data-testid={`scenario-row-${s.id}`}>
                                        <td className="px-3 py-2 font-display font-bold">{s.name}</td>
                                        <td className="px-3 py-2 text-white/50">GW{s.gw_start}–{s.gw_end}</td>
                                        <td className="px-3 py-2 text-white/70">{(s.planned_transfers || []).map((t) => t.in_name).join(", ") || "—"}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">£{s.metrics?.in_cost ?? 0}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-fa-mint">{s.metrics?.in_prev_points ?? 0}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{s.metrics?.avg_in_fdr ?? "—"}</td>
                                        <td className="px-3 py-2 text-white/50 uppercase text-[9px]">{s.chip || "—"}</td>
                                        <td className="px-3 py-2 text-orange-300/80 text-[9px] max-w-[180px] truncate">{(s.metrics?.risk_flags || []).join("; ") || "—"}</td>
                                        <td className="px-3 py-2">
                                            <button type="button" onClick={() => remove(s)} className="text-white/30 hover:text-red-400" data-testid={`scenario-delete-${s.id}`}><Trash size={12} /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className="space-y-3">
                <h2 className="font-display font-black uppercase text-base">Realizační nástroje</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="play-tools">
                    {TOOLS.map((t) => (
                        <Link key={t.label} to={t.to}
                              className="border border-white/10 bg-black/40 p-4 flex items-start gap-3 hover:border-fa-mint/40 transition-colors group">
                            <t.icon size={18} className="text-fa-mint flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="font-display font-bold text-sm group-hover:text-fa-mint transition-colors">{t.label}</div>
                                <div className="font-mono text-[9px] text-white/50 mt-0.5">{t.text}</div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            <section className="space-y-3">
                <h2 className="font-display font-black uppercase text-base">Manažerský trénink</h2>
                <Link to="/fun/price-guess" data-testid="play-price-guess-link"
                      className="border border-fa-yellow/30 bg-fa-yellow/5 p-4 flex items-start gap-3 hover:border-fa-yellow/60 transition-colors group max-w-xl">
                    <Tag size={18} className="text-fa-yellow flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="font-display font-bold text-sm group-hover:text-fa-yellow transition-colors">Cenovka — tipni cenu 26/27</div>
                        <div className="font-mono text-[9px] text-white/50 mt-0.5">Tipni novou cenu hráče a rozhodni, jestli za ni má value. 3 obtížnosti dle rychlosti a známosti hráčů — trénuj cit pro trh.</div>
                    </div>
                </Link>
            </section>
        </div>
    );
}
